import type { IntlShape, MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

import { formatRelativeTimeAgo } from '@confluence/time-ago';

import type { DevDocsFolderSyncMetadata, DevDocsFolderSyncStatus } from './devDocsSyncStatus.ts';

type DevDocsSyncStatusPresentationMessageKey = 'creatingDocs' | 'notSynced' | 'synced' | 'syncing';

export const devDocsSyncStatusPresentationMessages: Record<
	DevDocsSyncStatusPresentationMessageKey,
	MessageDescriptor
> = defineMessages({
	creatingDocs: {
		id: 'dev-docs.sync-status-presentation.creating-docs',
		defaultMessage: 'Creating docs',
		description: 'Status label while the first Dev Docs sync is being established.',
	},
	notSynced: {
		id: 'dev-docs.sync-status-presentation.not-synced',
		defaultMessage: 'Not synced',
		description: 'Status label when the latest Dev Docs folder sync failed.',
	},
	synced: {
		id: 'dev-docs.sync-status-presentation.synced',
		defaultMessage: 'Synced',
		description: 'Status label for a successfully synced Dev Docs folder.',
	},
	syncing: {
		id: 'dev-docs.sync-status-presentation.syncing',
		defaultMessage: 'Syncing',
		description: 'Status label while a Dev Docs folder sync is in progress.',
	},
});

export const devDocsSyncStatusMessages: Record<
	DevDocsFolderSyncStatus['status'],
	MessageDescriptor
> = {
	failed: devDocsSyncStatusPresentationMessages.notSynced,
	synced: devDocsSyncStatusPresentationMessages.synced,
	syncing: devDocsSyncStatusPresentationMessages.syncing,
	unknown: devDocsSyncStatusPresentationMessages.creatingDocs,
} as const;

export const devDocsSyncStatusAppearances: Record<
	DevDocsFolderSyncStatus['status'],
	'inprogress' | 'removed' | 'success'
> = {
	failed: 'removed',
	synced: 'success',
	syncing: 'inprogress',
	unknown: 'inprogress',
} as const;

export const formatDevDocsRelativeSyncTime = (
	timestamp: string | null,
	intl: IntlShape,
): string | null => {
	if (!timestamp) {
		return null;
	}

	const timestampMs = new Date(timestamp).getTime();
	if (Number.isNaN(timestampMs)) {
		return null;
	}

	return formatRelativeTimeAgo(timestampMs, intl, new Date(Date.now()));
};

export const getDevDocsSyncStatusTimestamp = (
	syncStatus: DevDocsFolderSyncStatus,
	syncMetadata?: DevDocsFolderSyncMetadata | null,
): string | null => {
	if (syncStatus.status === 'syncing') {
		return null;
	}

	return syncStatus.status === 'failed'
		? (syncMetadata?.latestSyncAttemptAt ?? syncMetadata?.lastSynced ?? null)
		: (syncMetadata?.lastSynced ?? null);
};
